import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import { SkeletonTable } from '../../components/common/Skeleton';
import { ShieldCheck, Wallet, Search, CheckCircle2, XCircle, Hash, Zap } from 'lucide-react';
import CopyButton from '../../components/common/CopyButton';

const WalletApprovals = () => {
  const [activeTab, setActiveTab] = useState('deposits'); // 'deposits' | 'activation'
  const [deposits, setDeposits] = useState([]);
  const [activationRequests, setActivationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Selected deposit for verification modal
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [selectedActivation, setSelectedActivation] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  const fetchAdminData = async () => {
    try {
      const [depRes, actRes] = await Promise.all([
        axiosClient.get('/wallet/admin/deposits'),
        axiosClient.get('/wallet/admin/activation-requests')
      ]);
      setDeposits(depRes.data.deposits || []);
      setActivationRequests(actRes.data.requests || []);
    } catch (err) {
      console.error('[Admin Wallet Approvals Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const handleVerifyDeposit = async (newStatus) => {
    if (!selectedDeposit) return;
    setVerifying(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.patch(`/wallet/admin/deposit/${selectedDeposit._id}/verify`, {
        status: newStatus,
        adminNotes
      });
      setFeedbackMsg({ 
        type: 'success', 
        text: `Deposit of $${selectedDeposit.amount} ${newStatus === 'approved' ? 'approved & credited to user wallet!' : 'rejected.'}` 
      });
      setSelectedDeposit(null);
      setAdminNotes('');
      fetchAdminData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to verify deposit request.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyActivation = async (actionType) => {
    if (!selectedActivation) return;
    setVerifying(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      const endpoint = actionType === 'approve'
        ? `/wallet/admin/activation/${selectedActivation._id}/approve`
        : `/wallet/admin/activation/${selectedActivation._id}/reject`;

      const res = await axiosClient.patch(endpoint, { adminNotes });
      setFeedbackMsg({ 
        type: 'success', 
        text: res.data.message || `Activation payment ${actionType}d successfully.` 
      });
      setSelectedActivation(null);
      setAdminNotes('');
      fetchAdminData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to process activation request.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setVerifying(false);
    }
  };

  const filteredDeposits = deposits.filter(dep => {
    const matchSearch = 
      dep.txid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dep.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dep.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus = statusFilter === 'all' || dep.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredActivationRequests = activationRequests.filter(act => {
    const matchSearch = 
      act.txid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      act.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      act.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      act.referrerId?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus = statusFilter === 'all' || act.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Title Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-5 h-5 text-[#F97316]" />
            <h2 className="text-lg font-extrabold text-[#FFFFFF]">USDT Deposit & Activation Verification Panel</h2>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Verify blockchain TXIDs for standard wallet deposits and platform account activation fee option deposits.
          </p>
        </div>

        {/* Search & Filter Controls */}
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search user, email, TXID..."
              className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-[#F97316]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#09090B] border border-[#27272A] text-[#FFFFFF] text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#F97316]"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending Verification</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {feedbackMsg.text && (
        <div className={`p-4 rounded-2xl border text-xs font-semibold ${
          feedbackMsg.type === 'success' 
            ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' 
            : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
        }`}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Tab Switcher */}
      <div className="flex items-center space-x-3 border-b border-[#27272A] pb-3">
        <button
          onClick={() => setActiveTab('deposits')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
            activeTab === 'deposits'
              ? 'bg-[#F97316] text-[#FFFFFF]'
              : 'bg-[#121215] text-[#9CA3AF] hover:bg-[#1A1A1E] border border-[#27272A]'
          }`}
        >
          <Wallet className="w-4 h-4" />
          <span>Standard Wallet Deposits ({filteredDeposits.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('activation')}
          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
            activeTab === 'activation'
              ? 'bg-[#F59E0B] text-[#FFFFFF]'
              : 'bg-[#121215] text-[#9CA3AF] hover:bg-[#1A1A1E] border border-[#27272A]'
          }`}
        >
          <Zap className="w-4 h-4 fill-current" />
          <span>⚡ Activation Fee Approvals ({filteredActivationRequests.length})</span>
        </button>
      </div>

      {activeTab === 'deposits' ? (
        /* Admin Standard Wallet Deposit Requests Table */
        <Card title={`Standard Wallet Deposits (${filteredDeposits.length})`} subtitle="Click Review to check TXID and credit user wallet balance">
          {loading ? (
            <SkeletonTable rows={4} cols={6} />
          ) : filteredDeposits.length === 0 ? (
            <div className="p-8 text-center text-xs text-[#9CA3AF]">No deposit requests match the filter criteria.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Amount ($)</th>
                    <th className="py-3 px-4">Network</th>
                    <th className="py-3 px-4">TXID Hash</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Submitted</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A] text-xs">
                  {filteredDeposits.map((dep) => (
                    <tr key={dep._id} className="hover:bg-[#1A1A1E] transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-bold text-[#FFFFFF]">{dep.userId?.name || 'Unknown'}</span>
                          <span className="font-mono text-[10px] text-[#F59E0B]">({dep.userId?.userIdTag})</span>
                          <CopyButton text={dep.userId?.userIdTag} />
                        </div>
                        <div className="flex items-center space-x-1.5 mt-0.5">
                          <span className="text-[10px] text-[#9CA3AF]">{dep.userId?.email}</span>
                          <CopyButton text={dep.userId?.email} />
                        </div>
                      </td>
                      <td className="py-3.5 px-4 font-bold text-[#10B981]">
                        +${dep.amount.toFixed(2)}
                      </td>
                      <td className="py-3.5 px-4 font-mono text-[#9CA3AF]">
                        {dep.network}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center space-x-1.5 font-mono text-[#F59E0B]">
                          <span>{dep.txid.substring(0, 14)}...</span>
                          <CopyButton text={dep.txid} />
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <StatusBadge status={dep.status} label={dep.status} />
                      </td>
                      <td className="py-3.5 px-4 text-[#9CA3AF]">
                        {new Date(dep.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <Button
                          variant={dep.status === 'pending' ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={() => {
                            setSelectedDeposit(dep);
                            setAdminNotes(dep.adminNotes || '');
                          }}
                        >
                          {dep.status === 'pending' ? 'Verify Deposit' : 'View Details'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        /* Activation Fee Deposit Approvals Table */
        <Card title={`Activation Fee Deposits (${filteredActivationRequests.length})`} subtitle="Verify TXID and process Admin / Direct Referrer split wallet credits">
          {loading ? (
            <SkeletonTable rows={4} cols={7} />
          ) : filteredActivationRequests.length === 0 ? (
            <div className="p-8 text-center text-xs text-[#9CA3AF]">No activation fee deposits found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                    <th className="py-3 px-4">User (Payer)</th>
                    <th className="py-3 px-4">Fee Amount</th>
                    <th className="py-3 px-4">TXID Hash</th>
                    <th className="py-3 px-4">Direct Referrer (Level 1)</th>
                    <th className="py-3 px-4">Referrer Cut</th>
                    <th className="py-3 px-4">Admin Cut</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A] text-xs">
                  {filteredActivationRequests.map((act) => {
                    const refRate = act.referrerSplitRate || 20;
                    const admRate = act.adminSplitRate || 80;
                    const referrerCut = act.referrerId ? ((act.amount * refRate) / 100).toFixed(2) : '0.00';
                    const adminCut = act.referrerId ? ((act.amount * admRate) / 100).toFixed(2) : act.amount.toFixed(2);

                    return (
                      <tr key={act._id} className="hover:bg-[#1A1A1E] transition-colors">
                        <td className="py-3.5 px-4">
                          <div className="flex items-center space-x-1.5">
                            <span className="font-bold text-[#FFFFFF]">{act.userId?.name || 'User'}</span>
                            <span className="font-mono text-[10px] text-[#F59E0B]">({act.userId?.userIdTag})</span>
                            <CopyButton text={act.userId?.userIdTag} />
                          </div>
                          <div className="flex items-center space-x-1.5 mt-0.5">
                            <span className="text-[10px] text-[#9CA3AF]">{act.userId?.email}</span>
                            <CopyButton text={act.userId?.email} />
                          </div>
                        </td>
                        <td className="py-3.5 px-4 font-extrabold text-[#F59E0B] font-mono">
                          ${act.amount.toFixed(2)} USDT
                        </td>
                        <td className="py-3.5 px-4 font-mono text-[#F59E0B]">
                          <div className="flex items-center space-x-1">
                            <span>{act.txid.substring(0, 12)}...</span>
                            <CopyButton text={act.txid} />
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          {act.referrerId ? (
                            <div>
                              <p className="font-bold text-[#FFFFFF]">{act.referrerId.name}</p>
                              <p className="text-[10px] text-[#9CA3AF]">{act.referrerId.email}</p>
                            </div>
                          ) : (
                            <span className="text-[#9CA3AF] text-[10px] italic">Direct Sign-up (No Referrer)</span>
                          )}
                        </td>
                        <td className="py-3.5 px-4 font-mono font-bold text-[#10B981]">
                          {act.referrerId ? `+$${referrerCut} (${refRate}%)` : '$0.00'}
                        </td>
                        <td className="py-3.5 px-4 font-mono font-bold text-[#3B82F6]">
                          +${adminCut} ({act.referrerId ? `${admRate}%` : '100%'})
                        </td>
                        <td className="py-3.5 px-4">
                          <StatusBadge status={act.status} label={act.status} />
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <Button
                            variant={act.status === 'pending' ? 'warning' : 'secondary'}
                            size="sm"
                            onClick={() => {
                              setSelectedActivation(act);
                              setAdminNotes(act.adminNotes || '');
                            }}
                          >
                            {act.status === 'pending' ? 'Approve Activation' : 'View Details'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Standard Deposit Verification Modal */}
      {selectedDeposit && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF]">Verify USDT Deposit Request</h3>
                <div className="flex items-center space-x-1 text-xs text-[#9CA3AF]">
                  <span>User:</span>
                  <span className="font-bold text-[#FFFFFF]">{selectedDeposit.userId?.name} ({selectedDeposit.userId?.email})</span>
                  <CopyButton text={selectedDeposit.userId?.email} />
                </div>
              </div>
              <button onClick={() => setSelectedDeposit(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <div className="p-4 bg-[#09090B] rounded-2xl border border-[#27272A] space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-[#9CA3AF]">Deposit Amount:</span>
                <span className="font-bold text-[#10B981]">${selectedDeposit.amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#9CA3AF]">Selected Network:</span>
                <span className="font-mono text-[#FFFFFF]">{selectedDeposit.network}</span>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#9CA3AF]">Transaction TXID Hash:</span>
                  <CopyButton text={selectedDeposit.txid} label="TXID" />
                </div>
                <p className="font-mono text-[#F59E0B] bg-[#121215] p-2 rounded-xl border border-[#27272A] break-all select-all">
                  {selectedDeposit.txid}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Verification Notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Enter notes (e.g. Blockchain TXID verified, wallet credited)"
                rows={2}
                disabled={selectedDeposit.status !== 'pending'}
                className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl p-3 text-xs focus:outline-none focus:border-[#F97316]"
              />
            </div>

            {selectedDeposit.status === 'pending' ? (
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#27272A]">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleVerifyDeposit('rejected')}
                  disabled={verifying}
                >
                  Reject Deposit
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleVerifyDeposit('approved')}
                  disabled={verifying}
                >
                  Approve & Credit Wallet
                </Button>
              </div>
            ) : (
              <div className="p-3 bg-[#09090B] rounded-xl text-center text-xs text-[#9CA3AF]">
                This deposit has already been <span className="font-bold uppercase text-[#FFFFFF]">{selectedDeposit.status}</span>.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activation Fee Verification Modal */}
      {selectedActivation && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF] flex items-center space-x-2">
                  <Zap className="w-4 h-4 text-[#F59E0B]" />
                  <span>Verify Account Activation Fee</span>
                </h3>
                <div className="flex items-center space-x-1 text-xs text-[#9CA3AF]">
                  <span>Payer:</span>
                  <span className="font-bold text-[#FFFFFF]">{selectedActivation.userId?.name} ({selectedActivation.userId?.email})</span>
                  <CopyButton text={selectedActivation.userId?.email} />
                </div>
              </div>
              <button onClick={() => setSelectedActivation(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <div className="p-4 bg-[#09090B] rounded-2xl border border-[#27272A] space-y-2.5 text-xs">
              <div className="flex justify-between">
                <span className="text-[#9CA3AF]">Activation Fee Paid:</span>
                <span className="font-bold text-[#F59E0B] font-mono">${selectedActivation.amount.toFixed(2)} USDT</span>
              </div>
              <div className="flex justify-between border-t border-[#27272A] pt-2">
                <span className="text-[#9CA3AF]">Direct Referrer (Level 1):</span>
                <span className="font-bold text-[#FFFFFF]">{selectedActivation.referrerId?.name || 'Direct Sign-up (None)'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#9CA3AF]">{selectedActivation.referrerSplitRate || 20}% Referrer Commission:</span>
                <span className="font-mono font-bold text-[#10B981]">
                  {selectedActivation.referrerId ? `+$${((selectedActivation.amount * (selectedActivation.referrerSplitRate || 20)) / 100).toFixed(2)} USDT` : '$0.00'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#9CA3AF]">{selectedActivation.referrerId ? `${selectedActivation.adminSplitRate || 80}%` : '100%'} Admin System Revenue:</span>
                <span className="font-mono font-bold text-[#3B82F6]">
                  +${selectedActivation.referrerId ? ((selectedActivation.amount * (selectedActivation.adminSplitRate || 80)) / 100).toFixed(2) : selectedActivation.amount.toFixed(2)} USDT
                </span>
              </div>
              <div className="pt-1">
                <span className="text-[#9CA3AF] block mb-1">Transaction TXID Hash:</span>
                <p className="font-mono text-[#F59E0B] bg-[#121215] p-2 rounded-xl border border-[#27272A] break-all select-all">
                  {selectedActivation.txid}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Admin Notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Enter notes (e.g. Blockchain TXID verified, user account activated)"
                rows={2}
                disabled={selectedActivation.status !== 'pending'}
                className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl p-3 text-xs focus:outline-none focus:border-[#F97316]"
              />
            </div>

            {selectedActivation.status === 'pending' ? (
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#27272A]">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleVerifyActivation('reject')}
                  disabled={verifying}
                >
                  Reject Activation
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleVerifyActivation('approve')}
                  disabled={verifying}
                >
                  Approve & Activate User
                </Button>
              </div>
            ) : (
              <div className="p-3 bg-[#09090B] rounded-xl text-center text-xs text-[#9CA3AF]">
                This activation request has already been <span className="font-bold uppercase text-[#FFFFFF]">{selectedActivation.status}</span>.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletApprovals;
